import { lazy, Suspense } from "react"
import { createBrowserRouter, Navigate, useLocation, useParams } from "react-router-dom"

import AnalyticsEpochs from "@/pages/analytics/epochs"
import AnalyticsIndex from "@/pages/analytics/index"
import AnalyticsVolume from "@/pages/analytics/volume"
import TreasuryIndex from "@/pages/treasury/index"
// Import all page components
import DashboardIndex from "@/pages/dashboard/index"
import DesignComponents from "@/pages/design/components"
import DesignComponentsTable from "@/pages/design/components/table"
import DesignDesignPalette from "@/pages/design/design-palette"
import DesignDesignSystem from "@/pages/design/design-system"
import DesignIndex from "@/pages/design/index"
import DesignTypography from "@/pages/design/typography"
import DesignUiSystem from "@/pages/design/ui-system"
import DiagnosticsIndex from "@/pages/diagnostics/index"
import DiagnosticsOrchestratedQueries from "@/pages/diagnostics/orchestrated-queries"
import IncentivizeIndex from "@/pages/incentivize/index"
import HomeIndex from "@/pages/index"
import LiquidityAddressAdd from "@/pages/liquidity/[address]/add"
import LiquidityAddressGaugeCreating from "@/pages/liquidity/[address]/gauge-creating"
import LiquidityAddressStake from "@/pages/liquidity/[address]/stake"
import LiquidityAddressUnstake from "@/pages/liquidity/[address]/unstake"
import LiquidityAddressWithdraw from "@/pages/liquidity/[address]/withdraw"
import LiquidityClFarmsAddressMint from "@/pages/liquidity/cl-farms/[address]/mint"
import LiquidityClFarmsAddressRedeem from "@/pages/liquidity/cl-farms/[address]/redeem"
import LiquidityClFarmsAddressStake from "@/pages/liquidity/cl-farms/[address]/stake"
import LiquidityClFarmsAddressUnstake from "@/pages/liquidity/cl-farms/[address]/unstake"
import LiquidityClFarmsIndex from "@/pages/liquidity/cl-farms/index"
import LiquidityCreate from "@/pages/liquidity/create"
import LiquidityCreating from "@/pages/liquidity/creating"
import LiquidityIndex from "@/pages/liquidity/index"
import LiquidityManualClAddressCreatePosition from "@/pages/liquidity/cl/v1/[address]/create-position"
import LiquidityManualClAddressDetail from "@/pages/liquidity/cl/v1/[address]/index"
import LiquidityManualClAddressManageIdClose from "@/pages/liquidity/cl/v1/[address]/manage/[id]/close"
import LiquidityManualClAddressManageIdCollect from "@/pages/liquidity/cl/v1/[address]/manage/[id]/collect"
import LiquidityManualClAddressManageIdDecrease from "@/pages/liquidity/cl/v1/[address]/manage/[id]/decrease"
import LiquidityManualClAddressManageIdIncrease from "@/pages/liquidity/cl/v1/[address]/manage/[id]/increase"
import LiquidityManualClAddressManageIdHistory from "@/pages/liquidity/cl/v1/[address]/manage/[id]/history"
import LiquidityManualClAddressManageIdOverview from "@/pages/liquidity/cl/v1/[address]/manage/[id]/index"
import LiquidityManualClCreate from "@/pages/liquidity/cl/v1/create"
import LiquidityManualClIndex from "@/pages/liquidity/cl/v1/index"
import LiquidityManualClV2AddressCreatePosition from "@/pages/liquidity/cl/v2/[address]/create-position"
import LiquidityManualClV2AddressDetail from "@/pages/liquidity/cl/v2/[address]/index"
import LiquidityManualClV2AddressManageIdClose from "@/pages/liquidity/cl/v2/[address]/manage/[id]/close"
import LiquidityManualClV2AddressManageIdCollect from "@/pages/liquidity/cl/v2/[address]/manage/[id]/collect"
import LiquidityManualClV2AddressManageIdDecrease from "@/pages/liquidity/cl/v2/[address]/manage/[id]/decrease"
import LiquidityManualClV2AddressManageIdIncrease from "@/pages/liquidity/cl/v2/[address]/manage/[id]/increase"
import LiquidityManualClV2AddressManageIdHistory from "@/pages/liquidity/cl/v2/[address]/manage/[id]/history"
import LiquidityManualClV2AddressManageIdOverview from "@/pages/liquidity/cl/v2/[address]/manage/[id]/index"
import LiquidityManualClV2Create from "@/pages/liquidity/cl/v2/create"
import LiquidityManualClV2Index from "@/pages/liquidity/cl/v2/index"
import LockIdExtendDuration from "@/pages/lock/[id]/extend-duration"
import LockIdIncreaseAmount from "@/pages/lock/[id]/increase-amount"
import LockIdMerge from "@/pages/lock/[id]/merge"
import LockIdReset from "@/pages/lock/[id]/reset"
import LockIdTransfer from "@/pages/lock/[id]/transfer"
import LockIdVenamm from "@/pages/lock/[id]/venamm"
import LockIdWithdraw from "@/pages/lock/[id]/withdraw"
import LockCreate from "@/pages/lock/create"
import LockIndex from "@/pages/lock/index"
import RewardsFees from "@/pages/rewards/fees"
import RewardsIndex from "@/pages/rewards/index"
import RewardsVoting from "@/pages/rewards/voting"
import SettingsIndex from "@/pages/settings/index"
import SwapIndex from "@/pages/swap/index"
import VoteIndex from "@/pages/vote/index"

import App from "./App"

// Lazy-loaded blog and docs routes
const BlogIndex = lazy(() => import("@/pages/blog/index"))
const BlogPost = lazy(() => import("@/pages/blog/[slug]"))
const DocsPage = lazy(() => import("@/pages/docs/[slug]"))

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="text-t100">Loading...</div>
  </div>
)

/**
 * Forwards any deep `/liquidity/manual-cl/<rest>` URL to its new
 * `/liquidity/cl/v1/<rest>` home, preserving `:address`, `:id`, and
 * `manage/.../(increase|decrease|collect|close|history)` segments plus any
 * `?query=string`. Keeping legacy links alive makes the route migration a
 * non-event for existing bookmarks and outbound Discord posts.
 */
const LegacyManualClRedirect = () => {
  const params = useParams()
  const location = useLocation()
  const rest = params["*"] ?? ""
  const target = rest ? `/liquidity/cl/v1/${rest}` : "/liquidity/cl/v1"
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      {
        index: true,
        element: <HomeIndex />,
      },
      {
        path: "dashboard",
        element: <DashboardIndex />,
      },
      {
        path: "swap",
        element: <SwapIndex />,
      },
      {
        path: "vote",
        element: <VoteIndex />,
      },
      {
        path: "settings",
        element: <SettingsIndex />,
      },
      {
        path: "incentivize",
        element: <IncentivizeIndex />,
      },
      {
        path: "rewards",
        element: <RewardsIndex />,
      },
      {
        path: "rewards/fees",
        element: <RewardsFees />,
      },
      {
        path: "rewards/voting",
        element: <RewardsVoting />,
      },
      {
        path: "lock",
        element: <LockIndex />,
      },
      {
        path: "lock/create",
        element: <LockCreate />,
      },
      {
        path: "lock/:id/extend-duration",
        element: <LockIdExtendDuration />,
      },
      {
        path: "lock/:id/increase-amount",
        element: <LockIdIncreaseAmount />,
      },
      {
        path: "lock/:id/merge",
        element: <LockIdMerge />,
      },
      {
        path: "lock/:id/reset",
        element: <LockIdReset />,
      },
      {
        path: "lock/:id/transfer",
        element: <LockIdTransfer />,
      },
      {
        path: "lock/:id/venamm",
        element: <LockIdVenamm />,
      },
      {
        path: "lock/:id/withdraw",
        element: <LockIdWithdraw />,
      },
      {
        path: "liquidity",
        element: <LiquidityIndex />,
      },
      {
        path: "liquidity/create",
        element: <LiquidityCreate />,
      },
      {
        path: "liquidity/creating",
        element: <LiquidityCreating />,
      },
      {
        path: "liquidity/:address/add",
        element: <LiquidityAddressAdd />,
      },
      {
        path: "liquidity/:address/gauge-creating",
        element: <LiquidityAddressGaugeCreating />,
      },
      {
        path: "liquidity/:address/stake",
        element: <LiquidityAddressStake />,
      },
      {
        path: "liquidity/:address/unstake",
        element: <LiquidityAddressUnstake />,
      },
      {
        path: "liquidity/:address/withdraw",
        element: <LiquidityAddressWithdraw />,
      },
      {
        path: "liquidity/cl-farms",
        element: <LiquidityClFarmsIndex />,
      },
      {
        path: "liquidity/cl-farms/:address/mint",
        element: <LiquidityClFarmsAddressMint />,
      },
      {
        path: "liquidity/cl-farms/:address/redeem",
        element: <LiquidityClFarmsAddressRedeem />,
      },
      {
        path: "liquidity/cl-farms/:address/stake",
        element: <LiquidityClFarmsAddressStake />,
      },
      {
        path: "liquidity/cl-farms/:address/unstake",
        element: <LiquidityClFarmsAddressUnstake />,
      },
      {
        path: "liquidity/cl/v1",
        element: <LiquidityManualClIndex />,
      },
      {
        path: "liquidity/cl/v1/create",
        element: <LiquidityManualClCreate />,
      },
      {
        path: "liquidity/cl/v1/:address",
        element: <LiquidityManualClAddressDetail />,
      },
      {
        path: "liquidity/cl/v1/:address/create-position",
        element: <LiquidityManualClAddressCreatePosition />,
      },
      {
        path: "liquidity/cl/v1/:address/manage/:id",
        element: <LiquidityManualClAddressManageIdOverview />,
      },
      {
        path: "liquidity/cl/v1/:address/manage/:id/decrease",
        element: <LiquidityManualClAddressManageIdDecrease />,
      },
      {
        path: "liquidity/cl/v1/:address/manage/:id/increase",
        element: <LiquidityManualClAddressManageIdIncrease />,
      },
      {
        path: "liquidity/cl/v1/:address/manage/:id/collect",
        element: <LiquidityManualClAddressManageIdCollect />,
      },
      {
        path: "liquidity/cl/v1/:address/manage/:id/close",
        element: <LiquidityManualClAddressManageIdClose />,
      },
      {
        path: "liquidity/cl/v1/:address/manage/:id/history",
        element: <LiquidityManualClAddressManageIdHistory />,
      },
      {
        path: "liquidity/cl/v2",
        element: <LiquidityManualClV2Index />,
      },
      {
        path: "liquidity/cl/v2/create",
        element: <LiquidityManualClV2Create />,
      },
      {
        path: "liquidity/cl/v2/:address",
        element: <LiquidityManualClV2AddressDetail />,
      },
      {
        path: "liquidity/cl/v2/:address/create-position",
        element: <LiquidityManualClV2AddressCreatePosition />,
      },
      {
        path: "liquidity/cl/v2/:address/manage/:id",
        element: <LiquidityManualClV2AddressManageIdOverview />,
      },
      {
        path: "liquidity/cl/v2/:address/manage/:id/decrease",
        element: <LiquidityManualClV2AddressManageIdDecrease />,
      },
      {
        path: "liquidity/cl/v2/:address/manage/:id/increase",
        element: <LiquidityManualClV2AddressManageIdIncrease />,
      },
      {
        path: "liquidity/cl/v2/:address/manage/:id/collect",
        element: <LiquidityManualClV2AddressManageIdCollect />,
      },
      {
        path: "liquidity/cl/v2/:address/manage/:id/close",
        element: <LiquidityManualClV2AddressManageIdClose />,
      },
      {
        path: "liquidity/cl/v2/:address/manage/:id/history",
        element: <LiquidityManualClV2AddressManageIdHistory />,
      },
      /*
       * Legacy /liquidity/manual-cl/* aliases — kept so bookmarks and
       * outbound Discord links don't 404 after the thick-v1 -> cl/v1 route
       * migration. Order matters: the deepest patterns must be listed
       * before the catch-all `liquidity/manual-cl/*` so React Router
       * matches the specific paths first and forwards the `:address` /
       * `:id` params onto the new URL.
       */
      {
        path: "liquidity/manual-cl",
        element: <Navigate to="/liquidity/cl/v1" replace />,
      },
      {
        path: "liquidity/manual-cl/create",
        element: <Navigate to="/liquidity/cl/v1/create" replace />,
      },
      {
        path: "liquidity/manual-cl/*",
        element: <LegacyManualClRedirect />,
      },
      {
        path: "design",
        element: <DesignIndex />,
      },
      {
        path: "design/components",
        element: <DesignComponents />,
      },
      {
        path: "design/components/table",
        element: <DesignComponentsTable />,
      },
      {
        path: "design/design-palette",
        element: <DesignDesignPalette />,
      },
      {
        path: "design/design-system",
        element: <DesignDesignSystem />,
      },
      {
        path: "design/typography",
        element: <DesignTypography />,
      },
      {
        path: "design/ui-system",
        element: <DesignUiSystem />,
      },
      {
        path: "analytics",
        element: <AnalyticsIndex />,
      },
      {
        path: "analytics/epochs",
        element: <AnalyticsEpochs />,
      },
      {
        path: "analytics/volume",
        element: <AnalyticsVolume />,
      },
      {
        path: "treasury",
        element: <TreasuryIndex />,
      },
      {
        path: "diagnostics",
        element: <DiagnosticsIndex />,
      },
      {
        path: "diagnostics/orchestrated-queries",
        element: <DiagnosticsOrchestratedQueries />,
      },
      {
        path: "blog",
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogIndex />
          </Suspense>
        ),
      },
      {
        path: "blog/:slug",
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogPost />
          </Suspense>
        ),
      },
      {
        path: "docs",
        element: <Navigate to="/docs/index" replace />,
      },
      {
        path: "docs/*",
        element: (
          <Suspense fallback={<PageLoader />}>
            <DocsPage />
          </Suspense>
        ),
      },
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
])
